import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationContext } from '@/contexts/OrganizationContext';

export type OrganizationType = 'corporate' | 'private_equity' | 'venture_capital' | 'consulting' | 'investment_banking';

export interface OrganizationProfile {
  id: string;
  company_name: string;
  website_url: string;
  industry: string;
  company_size?: string;
  ticker_symbol?: string;
  is_public_company?: boolean;
  revenue_model?: string;
  organization_type?: OrganizationType;
  
  // AI-discovered intelligence (JSONB columns — typed as structured records)
  ai_executive_summary?: string;
  ai_company_overview?: Record<string, unknown>;
  ai_products_services?: Record<string, unknown>;
  ai_strategic_priorities?: Record<string, unknown>;
  ai_business_model?: Record<string, unknown>;
  ai_target_customers?: Record<string, unknown>;
  ai_competitive_advantages?: Record<string, unknown>;
  ai_leadership_team?: Record<string, unknown>;
  ai_technology_stack?: Record<string, unknown>;
  ai_market_positioning?: Record<string, unknown>;
  ai_key_metrics?: Record<string, unknown>;
  ai_financials?: Record<string, unknown>;
  ai_financials_detailed?: Record<string, unknown>;
  ai_company_dna?: Record<string, unknown>;
  ai_products_deep?: Record<string, unknown>;
  ai_market_context?: Record<string, unknown>;
  ai_competitors_detailed?: Record<string, unknown>;
  ai_swot_analysis?: Record<string, unknown>;
  ai_strategic_outlook?: Record<string, unknown>;
  ai_hiring_signals?: Record<string, unknown>;
  ai_customer_intelligence?: Record<string, unknown>;
  ai_partnerships?: Record<string, unknown>;
  ai_sec_summary?: Record<string, unknown>;
  ai_earnings_highlights?: Record<string, unknown>;
  ai_news_summary?: Record<string, unknown>;
  ai_analyst_sentiment?: Record<string, unknown>;
  
  // White-label branding
  custom_logo_url?: string;
  primary_color?: string;
  accent_color?: string;
  custom_domain?: string;
  favicon_url?: string;
  white_label_enabled?: boolean;

  // Discovery metadata
  discovery_started_at?: string;
  discovery_completed_at?: string;
  last_refreshed_at?: string;
  confidence_scores?: Record<string, number>;
  discovered_competitor_ids?: string[];
  
  created_at?: string;
  updated_at?: string;
}

export function useOrganizationProfile() {
  const { user } = useAuth();
  const { activeOrganizationId, isOverriding } = useOrganizationContext();
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isViewingAsOtherOrg, setIsViewingAsOtherOrg] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        // If super admin is overriding, fetch that org directly
        if (activeOrganizationId && isOverriding) {
          const { data: orgProfile, error: orgError } = await supabase
            .from('organization_profiles')
            .select('*')
            .eq('id', activeOrganizationId)
            .single();

          if (orgError) throw orgError;

          setProfile(orgProfile as OrganizationProfile);
          setIsViewingAsOtherOrg(true);
          return;
        }

        // Default: fetch user's own organization
        const { data: userProfile, error: userError } = await supabase
          .from('user_profiles')
          .select('organization_profile_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (userError) throw userError;

        if (!userProfile?.organization_profile_id) {
          setProfile(null);
          setIsViewingAsOtherOrg(false);
          setLoading(false);
          return;
        }

        // Fetch the organization profile
        const { data: orgProfile, error: orgError } = await supabase
          .from('organization_profiles')
          .select('*')
          .eq('id', userProfile.organization_profile_id)
          .single();

        if (orgError) throw orgError;

        setProfile(orgProfile as OrganizationProfile);
        setIsViewingAsOtherOrg(false);
      } catch (err: unknown) {
        console.error('[useOrganizationProfile] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load organization profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user, activeOrganizationId, isOverriding]);

  const refetch = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // If super admin is overriding, fetch that org directly (mirrors useEffect logic)
      if (activeOrganizationId && isOverriding) {
        const { data: orgProfile, error: orgError } = await supabase
          .from('organization_profiles')
          .select('*')
          .eq('id', activeOrganizationId)
          .single();

        if (orgError) throw orgError;

        setProfile(orgProfile as OrganizationProfile);
        setIsViewingAsOtherOrg(true);
        return;
      }

      // Default: fetch user's own organization
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_profile_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (userProfile?.organization_profile_id) {
        const { data: orgProfile } = await supabase
          .from('organization_profiles')
          .select('*')
          .eq('id', userProfile.organization_profile_id)
          .single();

        setProfile(orgProfile as OrganizationProfile);
        setIsViewingAsOtherOrg(false);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return { profile, loading, error, refetch, isViewingAsOtherOrg };
}
